import type { ReactNode } from 'react';
import { Drawer, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconActivity, IconArrowLeft, IconListNumbers, IconPhone, IconSettings } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import type { LinkProps } from '@tanstack/react-router';
import { VoicemailBadge } from './voicemail-badge';
import { isNavItemActive } from './nav-links';
import type { NavItem } from './nav-links';

function DrawerLink({
  to,
  label,
  icon: Icon,
  isActive,
  onNavigate,
  children,
}: {
  to: NonNullable<LinkProps['to']>;
  label: string;
  icon: NavItem['icon'];
  isActive: boolean;
  onNavigate: () => void;
  children?: ReactNode;
}) {
  return (
    <UnstyledButton
      component={Link}
      to={to}
      onClick={onNavigate}
      p="sm"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--mantine-spacing-sm)',
        borderRadius: 'var(--mantine-radius-md)',
        backgroundColor: isActive ? 'var(--mantine-color-indigo-light)' : 'transparent',
        color: isActive ? 'var(--mantine-color-indigo-text)' : 'var(--mantine-color-text)',
        fontWeight: isActive ? 600 : 400,
      }}
    >
      <Icon size={20} stroke={1.5} />
      <Text size="sm">{label}</Text>
      {children}
    </UnstyledButton>
  );
}

export function NavDrawer({
  opened,
  onClose,
  navLinks,
  pathname,
  organizationId,
  showAdminLinks,
  isSystemPage,
}: {
  opened: boolean;
  onClose: () => void;
  navLinks: NavItem[];
  pathname: string;
  organizationId: string | null;
  showAdminLinks: boolean;
  isSystemPage: boolean;
}) {
  return (
    <Drawer opened={opened} onClose={onClose} size="100%" padding="md" hiddenFrom="md" zIndex={1000000}>
      <Stack gap="xs">
        {navLinks.map((item) => (
          <DrawerLink
            key={item.label}
            to={item.link}
            label={item.label}
            icon={item.icon}
            isActive={isNavItemActive(pathname, item.link)}
            onNavigate={onClose}
          >
            {item.link === '/voicemails' && <VoicemailBadge organizationId={organizationId} />}
          </DrawerLink>
        ))}

        {showAdminLinks && (
          <>
            <DrawerLink
              to="/active-dashboard"
              label="Active Dashboard"
              icon={IconActivity}
              isActive={false}
              onNavigate={onClose}
            />
            <DrawerLink to="/calls" label="Calls" icon={IconPhone} isActive={false} onNavigate={onClose} />
            <DrawerLink
              to="/queue-dashboard"
              label="Queue Dashboard"
              icon={IconListNumbers}
              isActive={false}
              onNavigate={onClose}
            />
            <DrawerLink to="/settings" label="Settings" icon={IconSettings} isActive={false} onNavigate={onClose} />
          </>
        )}

        {isSystemPage && (
          <DrawerLink to="/" label="Exit System" icon={IconArrowLeft} isActive={false} onNavigate={onClose} />
        )}
      </Stack>
    </Drawer>
  );
}
